'use client'

import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import clsx from 'clsx'

import { useOverlayStack } from 'shared/contexts/OverlayStackContext'
import { useFocusTrap } from 'shared/ui/hooks/useFocusTrap'
import { KEYBOARD_KEYS, ZIndex } from 'shared/ui/types'
import { isClient } from 'shared/utils/common'

import { type AnchorOrigin, getPopoverPosition } from './utils'

import styles from './Popover.module.scss'

const DEFAULT_ANCHOR_ORIGIN: AnchorOrigin = {
  vertical: 'bottom',
  horizontal: 'right'
}

export interface PopoverProps extends React.HTMLAttributes<HTMLDivElement> {
  id: string
  open: boolean
  onClose: () => void
  anchorEl: React.RefObject<HTMLElement | null>
  children: React.ReactNode
  anchorOrigin?: AnchorOrigin
  zIndex?: number
  className?: string
  backdrop?: boolean
  distanceFromAnchor?: number
}

const Popover = ({
  id,
  open,
  onClose,
  anchorEl,
  children,
  anchorOrigin = DEFAULT_ANCHOR_ORIGIN,
  zIndex = ZIndex.POPOVER,
  className,
  backdrop = false,
  distanceFromAnchor,
  style,
  ...props
}: PopoverProps) => {
  const [popoverElement, setPopoverElement] = useState<HTMLDivElement | null>(
    null
  )
  const [position, setPosition] = useState<{
    top: number
    left: number
  } | null>(null)
  const [isMeasured, setIsMeasured] = useState(false)

  useFocusTrap({
    container: popoverElement,
    active: open,
    skipInitialFocus: true,
    previouslyFocusedElement: anchorEl
  })
  const { register, unregister, isTop, getZIndex } = useOverlayStack()

  useEffect(() => {
    if (open) {
      register({ id, type: 'popover' })
    } else {
      unregister(id)
    }

    return () => {
      unregister(id)
    }
  }, [open, register, unregister, id])

  // Position calculation (reuse SelectList pattern: RAF + resize/scroll listeners)
  useEffect(() => {
    if (!open || !anchorEl || !popoverElement) {
      setIsMeasured(false)
      setPosition(null)

      return
    }

    const updatePosition = () => {
      if (!anchorEl || !popoverElement) return

      const anchorRect = anchorEl.current?.getBoundingClientRect()

      if (!anchorRect) return

      const contentRect = popoverElement.getBoundingClientRect()

      const nextPosition = getPopoverPosition(
        anchorRect,
        contentRect.width,
        contentRect.height,
        anchorOrigin,
        distanceFromAnchor
      )

      setPosition(nextPosition)
      setIsMeasured(true)
    }

    requestAnimationFrame(() => {
      updatePosition()
    })

    const handleResizeOrScroll = () => {
      updatePosition()
    }

    window.addEventListener('scroll', handleResizeOrScroll, true)
    window.addEventListener('resize', handleResizeOrScroll)

    return () => {
      window.removeEventListener('scroll', handleResizeOrScroll, true)
      window.removeEventListener('resize', handleResizeOrScroll)
    }
  }, [open, anchorEl, popoverElement, anchorOrigin, distanceFromAnchor])

  // Close on Escape
  useEffect(() => {
    if (open && isTop(id)) {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === KEYBOARD_KEYS.ESCAPE) {
          e.preventDefault()
          onClose()
        }
      }

      document.addEventListener('keydown', handleKeyDown)

      return () => document.removeEventListener('keydown', handleKeyDown)
    }

    return
  }, [open, onClose, isTop, id])

  const finalZIndex = useMemo(
    () => zIndex ?? (getZIndex(id) || ZIndex.POPOVER),
    [getZIndex, id, zIndex]
  )

  if (!open || !isClient()) return null

  return createPortal(
    <>
      <div
        className={clsx(styles.backdrop, {
          [styles.backdropVisible]: backdrop
        })}
        style={{ zIndex: finalZIndex }}
        onClick={onClose}
        aria-hidden={open}
      />

      <div
        ref={setPopoverElement}
        id={id}
        role='dialog'
        aria-modal={open}
        className={clsx(styles.paper, className)}
        tabIndex={-1}
        onClick={e => e.stopPropagation()}
        {...props}
        style={{
          top: position ? `${position.top}px` : '0',
          left: position ? `${position.left}px` : '0',
          zIndex: finalZIndex + 1,
          visibility: isMeasured ? 'visible' : 'hidden',
          ...style
        }}
      >
        {children}
      </div>
    </>,
    document?.getElementById('overlay-portal') || document?.body
  )
}

export default Popover
